/**
 * Voice TTS Manager
 * Manages multiple TTS instances and ensures only one message speaks at a time
 * 
 * @author Software Engineering Team
 * @version 1.0.0
 */

import VoiceTTSService from './VoiceTTSService';

class VoiceTTSManager {
  constructor() {
    this.ttsService = null;
    this.currentMessageId = null;
    this.messageStates = new Map(); // messageId -> { isPlaying, isPaused, text }
    this.callbacks = new Map(); // messageId -> callbacks
    
    this.initialize();
  }

  /**
   * Initialize the TTS manager
   */
  async initialize() {
    try {
      this.ttsService = new VoiceTTSService();
      const initialized = await this.ttsService.initialize();
      
      if (initialized) {
        this.setupGlobalCallbacks();
        console.info('VoiceTTSManager initialized successfully');
      }
      
      return initialized;
    } catch (error) {
      console.error('Failed to initialize VoiceTTSManager:', error);
      return false;
    }
  }

  /**
   * Setup global TTS callbacks
   * @private
   */
  setupGlobalCallbacks() {
    this.ttsService.setCallbacks({
      onStart: () => {
        if (this.currentMessageId) {
          this.updateMessageState(this.currentMessageId, { isPlaying: true, isPaused: false });
          this.notifyMessage(this.currentMessageId, 'onStart');
        }
      },
      
      onEnd: () => {
        if (this.currentMessageId) {
          this.updateMessageState(this.currentMessageId, { isPlaying: false, isPaused: false });
          this.notifyMessage(this.currentMessageId, 'onEnd');
          this.currentMessageId = null;
        }
      },
      
      onError: (error) => {
        if (this.currentMessageId) {
          this.updateMessageState(this.currentMessageId, { isPlaying: false, isPaused: false });
          this.notifyMessage(this.currentMessageId, 'onError', error);
          this.currentMessageId = null;
        }
      },
      
      onPause: () => {
        if (this.currentMessageId) {
          this.updateMessageState(this.currentMessageId, { isPaused: true });
          this.notifyMessage(this.currentMessageId, 'onPause');
        }
      },
      
      onResume: () => {
        if (this.currentMessageId) {
          this.updateMessageState(this.currentMessageId, { isPaused: false });
          this.notifyMessage(this.currentMessageId, 'onResume');
        }
      }
    });
  }

  /**
   * Register a message for TTS
   * @param {string} messageId - Unique message identifier
   * @param {string} text - Text content to speak
   * @param {Object} callbacks - Message-specific callbacks
   */
  registerMessage(messageId, text, callbacks = {}) {
    this.messageStates.set(messageId, {
      isPlaying: false,
      isPaused: false,
      text: text
    });
    
    this.callbacks.set(messageId, callbacks);
    console.info(`Registered message ${messageId} for TTS`);
  }

  /**
   * Unregister a message from TTS
   * @param {string} messageId - Message identifier
   */
  unregisterMessage(messageId) {
    // Stop if this message is currently playing
    if (this.currentMessageId === messageId) {
      this.stop();
    }
    
    this.messageStates.delete(messageId);
    this.callbacks.delete(messageId);
    console.info(`Unregistered message ${messageId} from TTS`);
  }

  /**
   * Start speaking a message
   * @param {string} messageId - Message identifier
   * @returns {Promise<void>}
   */
  async speak(messageId) {
    try {
      if (!this.ttsService) {
        throw new Error('TTS service not initialized');
      }

      const messageState = this.messageStates.get(messageId);
      if (!messageState) {
        throw new Error(`Message ${messageId} not registered`);
      }

      // Stop any currently playing message
      if (this.currentMessageId && this.currentMessageId !== messageId) {
        this.stop();
      }

      // If this message is already playing, toggle pause/resume
      if (this.currentMessageId === messageId) {
        if (messageState.isPaused) {
          this.resume();
        } else if (messageState.isPlaying) {
          this.pause();
        }
        return;
      }

      // Start speaking this message
      this.currentMessageId = messageId;
      await this.ttsService.speak(messageState.text);
      
    } catch (error) {
      console.error(`Failed to speak message ${messageId}:`, error);
      this.notifyMessage(messageId, 'onError', error);
    }
  }

  /**
   * Pause current speech
   */
  pause() {
    if (this.ttsService && this.currentMessageId) {
      this.ttsService.pause();
    }
  }

  /**
   * Resume current speech
   */
  resume() {
    if (this.ttsService && this.currentMessageId) {
      this.ttsService.resume();
    }
  }

  /**
   * Stop current speech
   */
  stop() {
    if (this.ttsService) {
      this.ttsService.stop();
    }
    
    if (this.currentMessageId) {
      this.updateMessageState(this.currentMessageId, { isPlaying: false, isPaused: false });
      this.notifyMessage(this.currentMessageId, 'onEnd');
      this.currentMessageId = null;
    }
  }

  /**
   * Stop all messages and clear state
   */
  stopAll() {
    this.stop();
    
    // Reset all message states
    for (const messageId of this.messageStates.keys()) {
      this.updateMessageState(messageId, { isPlaying: false, isPaused: false });
      this.notifyMessage(messageId, 'onEnd');
    }
  }

  /**
   * Update message state
   * @param {string} messageId - Message identifier
   * @param {Object} updates - State updates
   * @private
   */
  updateMessageState(messageId, updates) {
    const currentState = this.messageStates.get(messageId);
    if (currentState) {
      this.messageStates.set(messageId, { ...currentState, ...updates });
    }
  }

  /**
   * Notify message callbacks
   * @param {string} messageId - Message identifier
   * @param {string} event - Event name
   * @param {*} data - Event data
   * @private
   */
  notifyMessage(messageId, event, data = null) {
    const callbacks = this.callbacks.get(messageId);
    if (callbacks && typeof callbacks[event] === 'function') {
      callbacks[event](data);
    }
  }

  /**
   * Get message state
   * @param {string} messageId - Message identifier
   * @returns {Object|null} - Message state
   */
  getMessageState(messageId) {
    return this.messageStates.get(messageId) || null;
  }

  /**
   * Check if any message is currently playing
   * @returns {boolean} - Playing status
   */
  isAnyPlaying() {
    return this.currentMessageId !== null;
  }

  /**
   * Get currently playing message ID
   * @returns {string|null} - Current message ID
   */
  getCurrentMessageId() {
    return this.currentMessageId;
  }

  /**
   * Update TTS configuration
   * @param {Object} config - New configuration
   */
  updateConfig(config) {
    if (this.ttsService) {
      this.ttsService.updateConfig(config);
    }
  }

  /**
   * Get available voices
   * @param {string} language - Language filter (optional)
   * @returns {Array} - Available voices
   */
  getVoices(language = null) {
    if (this.ttsService) {
      return this.ttsService.getVoices(language);
    }
    return [];
  }

  /**
   * Get TTS service status
   * @returns {Object} - Service status
   */
  getStatus() {
    const status = this.ttsService ? this.ttsService.getStatus() : {};
    return {
      ...status,
      currentMessageId: this.currentMessageId,
      registeredMessages: this.messageStates.size,
      isAnyPlaying: this.isAnyPlaying()
    };
  }

  /**
   * Cleanup resources
   */
  destroy() {
    this.stopAll();
    
    if (this.ttsService) {
      this.ttsService.destroy();
      this.ttsService = null;
    }
    
    this.messageStates.clear();
    this.callbacks.clear();
    this.currentMessageId = null;
    
    console.info('VoiceTTSManager destroyed');
  }
}

// Global instance for the application
let globalTTSManager = null;

/**
 * Get the global TTS manager instance
 * @returns {VoiceTTSManager} - Global TTS manager
 */
export const getTTSManager = () => {
  if (!globalTTSManager) {
    globalTTSManager = new VoiceTTSManager();
  }
  return globalTTSManager;
};

/**
 * Cleanup global TTS manager
 */
export const cleanupTTSManager = () => {
  if (globalTTSManager) {
    globalTTSManager.destroy();
    globalTTSManager = null;
  }
};

export default VoiceTTSManager;
